import React, { useState, useRef, useEffect, useCallback } from "react";
import DeskTopView from "../../Desktopview";
import RotateDeviceScreen from "../RotateDeviceScreen";
import FullscreenCapturePrompt from "../FullscreenCapturePrompt";
import {
  getViewportScreenMode,
  subscribeViewportOrientation,
} from "../../utils/viewportOrientation";
import {
  enterImmersiveMode,
  exitImmersiveMode,
  isImmersiveActive,
  subscribeFullscreenChange,
} from "../../utils/immersiveMode";
import {
  openCameraStream,
  getGetUserMedia,
  isSecureCameraContext,
  getCameraOriginHint,
  compressImageDataUrl,
} from "../../utils/cameraAccess";
import { useMobilePhotoDevice } from "../../utils/isMobilePhotoDevice";
import "../../CameraWithOverlayVideo/styles.css";
import "../FullscreenCapturePrompt/style.css";
import "../../camera_question/style.css";

/**
 * Shared landscape capture chrome used by Damage Points + Car Damages cameras.
 * Live getUserMedia only — requires HTTPS (or localhost).
 */
export default function NativeInspectionCamera({
  onCapture,
  onCancel,
  overlay = null,
  titleBar = null,
  hint = "Photograph the vehicle, then tap capture.",
  rotateDescription = "Hold your phone horizontally to capture photos.",
  captureEnabled = true,
  keepStreamAfterCapture = false,
  cancelLabel = "Cancel",
  compress = true,
  desktopTitle = null,
  desktopBadge = null,
  desktopLabel = "Device",
}) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const captureRootRef = useRef(null);
  const closingRef = useRef(false);
  const [sreen, setsreen] = useState(getViewportScreenMode);
  const [error, setError] = useState(null);
  const [ready, setReady] = useState(false);
  const [starting, setStarting] = useState(false);
  const [busy, setBusy] = useState(false);
  const apiAvailable = Boolean(getGetUserMedia());
  const isMobileDevice = useMobilePhotoDevice();

  const stopStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setReady(false);
  }, []);

  const startStream = useCallback(async () => {
    if (!isMobileDevice) return;
    if (sreen !== "landscap" || closingRef.current) return;
    if (!getGetUserMedia()) {
      setStarting(false);
      setReady(false);
      setError(
        isSecureCameraContext()
          ? "Camera API is blocked. Allow camera in browser settings, then tap Retry."
          : `Camera needs HTTPS (now on ${getCameraOriginHint()}). Open this app with https:// and tap Retry.`
      );
      return;
    }

    setStarting(true);
    setError(null);
    stopStream();

    try {
      const stream = await openCameraStream();
      if (closingRef.current) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }
      streamRef.current = stream;
      const video = videoRef.current;
      if (!video) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }
      video.srcObject = stream;
      video.setAttribute("playsinline", "true");
      video.setAttribute("webkit-playsinline", "true");
      video.muted = true;
      await video.play();
      setReady(true);
      setError(null);
    } catch (err) {
      const name = err?.name || "";
      if (err?.code === "CAMERA_API_UNAVAILABLE") {
        setError(err.message);
      } else if (name === "NotAllowedError" || name === "PermissionDeniedError") {
        setError("Camera permission denied. Allow camera access and tap Retry.");
      } else if (name === "NotFoundError" || name === "DevicesNotFoundError") {
        setError("No camera detected. Close other camera apps and tap Retry.");
      } else if (name === "NotReadableError" || name === "TrackStartError") {
        setError("Camera is busy. Close other apps and tap Retry.");
      } else {
        setError(err?.message || "Could not start camera. Tap Retry.");
      }
      setReady(false);
    } finally {
      setStarting(false);
    }
  }, [sreen, stopStream, isMobileDevice]);

  useEffect(() => subscribeViewportOrientation(setsreen), []);

  useEffect(() => {
    if (!isMobileDevice) {
      stopStream();
      exitImmersiveMode();
      return undefined;
    }

    if (sreen !== "landscap") {
      exitImmersiveMode();
      stopStream();
      return undefined;
    }

    let cancelled = false;
    let restartTimer;

    const run = () => {
      if (!cancelled && !closingRef.current) startStream();
    };

    // Do not auto-request fullscreen here — browsers require a real tap.
    run();

    const unsubFs = subscribeFullscreenChange(() => {
      clearTimeout(restartTimer);
      restartTimer = setTimeout(run, 350);
    });

    return () => {
      cancelled = true;
      clearTimeout(restartTimer);
      unsubFs();
      stopStream();
      exitImmersiveMode();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sreen, isMobileDevice]);

  const closeCamera = async (event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (closingRef.current) return;
    closingRef.current = true;
    stopStream();
    try {
      await exitImmersiveMode();
    } catch {
      /* ignore */
    }
    try {
      if (typeof onCancel === "function") {
        await onCancel();
      }
    } finally {
      closingRef.current = false;
    }
  };

  const requestFullscreenFromGesture = (event) => {
    if (event?.target?.closest?.("button, a, input, label, [role='button']")) {
      return;
    }
    if (isImmersiveActive(captureRootRef.current)) return;
    enterImmersiveMode(captureRootRef.current || document.documentElement);
  };

  const prepareImage = async (dataUrl) => {
    if (!compress) return dataUrl;
    try {
      return (await compressImageDataUrl(dataUrl, 1280, 0.72)) || dataUrl;
    } catch {
      return dataUrl;
    }
  };

  const finishWithImage = async (dataUrl) => {
    if (!dataUrl) return;
    setBusy(true);
    try {
      const next = await prepareImage(dataUrl);
      if (!keepStreamAfterCapture) {
        stopStream();
        await exitImmersiveMode();
      }
      if (typeof onCapture === "function") {
        await onCapture(next);
      }
    } catch (err) {
      setError(err?.message || "Could not capture photo. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const Uploadpic = async () => {
    const video = videoRef.current;
    if (!video || !ready || video.videoWidth < 2) {
      setError("Camera is not ready yet. Wait a moment, then tap Retry.");
      return;
    }
    try {
      const canvas = document.createElement("canvas");
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      const img = canvas.toDataURL("image/jpeg", 0.92);
      await finishWithImage(img);
    } catch (err) {
      setError("Could not capture photo. Please try again.");
    }
  };

  const statusText = starting
    ? "Starting camera…"
    : busy
      ? "Saving photo…"
      : ready
        ? hint
        : apiAvailable
          ? "Waiting for camera…"
          : "Open this page over HTTPS to use the camera";

  const barText =
    (busy ? "Saving photo…" : null) || titleBar || error || statusText;

  return (
    <>
      {!isMobileDevice ? (
        <DeskTopView
          onBack={closeCamera}
          title={desktopTitle}
          badge={desktopBadge}
          label={desktopLabel}
        />
      ) : sreen === "landscap" ? (
          <div
            ref={captureRootRef}
            className="container_ pi-inspection-camera"
            onClick={requestFullscreenFromGesture}
          >
            <div className="pi-camera-chrome">
              <FullscreenCapturePrompt targetRef={captureRootRef} />
              <button
                type="button"
                className="pi-camera-cancel"
                onClick={closeCamera}
                onTouchEnd={(event) => {
                  event.stopPropagation();
                }}
              >
                {cancelLabel}
              </button>
            </div>

            <div className="camera_main_cont">
              <div className="camera_wrapper">
                {apiAvailable ? (
                  <video
                    ref={videoRef}
                    className="pi-camera-video"
                    autoPlay
                    playsInline
                    muted
                  />
                ) : (
                  <div className="pi-camera-fallback" aria-hidden>
                    <p>Camera needs HTTPS</p>
                    <p className="pi-camera-fallback__hint">
                      Open this page with https:// on your phone, then tap Retry
                    </p>
                  </div>
                )}
                {overlay}
                <div className="video_wrapper pi-camera-title-layer">
                  <div className="bottom_title_bar flex_center w-100">
                    <div>{barText}</div>
                  </div>
                </div>
              </div>

              <div className="tool_bar">
                {busy ? (
                  <div className="pi-camera-waiting" aria-live="polite">
                    Saving…
                  </div>
                ) : !captureEnabled ? (
                  <div className="pi-camera-waiting" aria-live="polite">
                    Watch guide…
                  </div>
                ) : ready ? (
                  <button
                    type="button"
                    className="round_button flex_center"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      Uploadpic();
                    }}
                    aria-label="Capture photo"
                  >
                    <div className="round_button_ flex_center" />
                  </button>
                ) : (
                  <button
                    type="button"
                    className="pi-camera-retry"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      startStream();
                    }}
                  >
                    Retry
                  </button>
                )}
              </div>
            </div>
          </div>
        ) : (
          <RotateDeviceScreen
            onBack={closeCamera}
            description={rotateDescription}
          />
        )}
    </>
  );
}
